import {
  RadioGroup,
  SpinnerLegacy,
  StockIndicator,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components";
import { NavigationBarMenu } from "../../../components/navigation/NavigationBar";
import { Switch } from "@sellar-io/components";
import { useSupplier } from "../../../hooks/api/v2/supplier/useSupplier";
import { useSupplierUpdate } from "../../../hooks/api/v2/supplier/useSupplierUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { StorefrontSettingsImageUpload } from "./StorefrontSettingsImageUpload";
import { StorefrontSettingsPaymentNotesCard } from "./StorefrontSettingsPaymentNotesCard";
import { StorefrontSettingSupplierOutOfOffice } from "./StorefrontSettingsSupplierOutOfOffice";
import { StorefrontSettingSupplierAnnouncementCard } from "./StorefrontSettingSupplierAnnouncementCard";
import { StorefrontAvailabilityCard } from "./StorefrontSettingsStorefrontAvailabilityCard/StorefrontSettingsStorefrontAvailabilityCard";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarUser } from "../../../hooks";
import { Alert } from "../../../components/alert/Alert";
import {
  Anchor,
  Button,
  Card,
  CardContent,
  H5,
  H6,
  Icon,
} from "@sellar-io/components";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";

export const StorefrontSettings = () => {
  const navigate = useNavigate();
  const { user } = useSellarUser();
  const isUserSellarAdmin = user?.type === "admin";
  const { business } = useSellarCurrentBusiness();

  if (!business?.Supplier) throw new Error("Cannot find supplier");

  const supplierQuery = useSupplier(business.Supplier.id);

  const updateSupplierMutation = useSupplierUpdate(business.Supplier.id);

  const remoteFeatureFlagQuery = useRemoteFeatureFlags();

  return (
    <>
      <NavigationBarMenu title={"Settings"} />
      <div className={"m-auto flex max-w-225 flex-col gap-4 py-4"}>
        {supplierQuery.isLoading ? (
          <SpinnerLegacy />
        ) : supplierQuery.isError ? (
          <div>Something went wrong</div>
        ) : !supplierQuery.isSuccess ? null : (
          <>
            <div className="grid grid-cols-3 gap-8">
              <div className="col-span-1">
                <div className="flex flex-col">
                  <H5>Brand material</H5>
                  <H6>
                    Upload brand assets and provide a brief description about
                    your company
                  </H6>
                </div>
              </div>
              <div className="col-span-2">
                <StorefrontSettingsImageUpload supplier={supplierQuery.data} />
              </div>
            </div>

            {!remoteFeatureFlagQuery.isActive("unbundlingReleaseFinal") && (
              <div className="grid grid-cols-3 gap-8">
                <div className="col-span-1">
                  <div className="flex flex-col">
                    <H5>Default price tiers</H5>
                    <H6>
                      Control the products and pricing which your customers see.
                    </H6>
                  </div>
                </div>
                <div className="col-span-2">
                  <Alert
                    type="information"
                    title="Looking to view or edit your default price tiers?"
                    message={
                      <p>
                        You can now control what products and pricing your local
                        and nationwide customers see in your Customer Zones{" "}
                        <Anchor
                          className="inline-flex items-center gap-1 text-blue-600 underline"
                          href="https://intercom.help/sellario/en/articles/12011312-supplier-guide-customer-zones"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Learn more about your Customer Zones here
                          <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
                        </Anchor>
                      </p>
                    }
                  >
                    <Button
                      color="secondary"
                      className="mt-4 border-blue-800 bg-transparent text-blue-800 hover:bg-blue-200"
                      onClick={() => navigate(ROUTES.CUSTOMER_ZONES)}
                    >
                      Go to Customer Zones
                    </Button>
                  </Alert>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-8">
              <div className="col-span-1">
                <div className="flex flex-col">
                  <H5>Customise</H5>
                  <H6>Control the content and appearance of your storefront</H6>
                </div>
              </div>
              <div className="col-span-2">
                <div className="flex flex-col gap-2">
                  <Card className="bg-white">
                    <CardContent className={"flex flex-col gap-4 p-4"}>
                      <div className="flex flex-row justify-between">
                        <H5>Display stock count</H5>
                        <Switch
                          checked={supplierQuery.data.showStock}
                          onChange={(checked) => {
                            updateSupplierMutation.mutate({
                              showStock: checked,
                            });
                          }}
                        />
                      </div>
                      <H6>
                        Choose whether customers see the actual stock count for
                        products on your storefront
                      </H6>
                      <div className="flex flex-wrap gap-2">
                        <StockIndicator
                          type={"full"}
                          stock={15}
                          showStock={supplierQuery.data.showStock}
                        />
                        <StockIndicator
                          type={"full"}
                          stock={5}
                          showStock={supplierQuery.data.showStock}
                        />
                        <StockIndicator
                          stock={0}
                          showStock={supplierQuery.data.showStock}
                        />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white">
                    <CardContent className={"flex flex-col gap-4 p-4"}>
                      <div className="flex flex-row justify-between">
                        <H5>Out of stock products</H5>
                      </div>
                      <H6>
                        Products which are not hidden or inactive but out of
                        stock can be either shown or hidden by default when
                        customers first view your storefront. Note: your
                        customers can still show or hide products that are out
                        of stock when viewing your storefront. This option just
                        controls how your storefront loads by default.
                      </H6>
                      <div className="w-full">
                        <RadioGroup
                          value={
                            supplierQuery.data.showOutOfStockProducts
                              ? "yes"
                              : "no"
                          }
                          onChange={(value) => {
                            updateSupplierMutation.mutate({
                              showOutOfStockProducts: value === "yes",
                            });
                          }}
                        >
                          <RadioGroupContainer>
                            <RadioGroupOption value={"no"}>
                              <H5>
                                Hide products that are out of stock from
                                customers
                              </H5>
                            </RadioGroupOption>
                            <RadioGroupOption value={"yes"}>
                              <H5>
                                Show products that are out of stock to customers
                              </H5>
                            </RadioGroupOption>
                          </RadioGroupContainer>
                        </RadioGroup>
                      </div>
                    </CardContent>
                  </Card>
                  <StorefrontSettingSupplierOutOfOffice
                    supplier={supplierQuery.data}
                  />
                  <StorefrontSettingSupplierAnnouncementCard
                    supplier={supplierQuery.data}
                  />
                  <StorefrontSettingsPaymentNotesCard
                    paymentNotes={supplierQuery.data.paymentNotes}
                  />
                </div>
              </div>
            </div>

            {remoteFeatureFlagQuery.isActive("timeLimitedStorefrontDrop") &&
              isUserSellarAdmin && (
                <div className="grid grid-cols-3 gap-8">
                  <div className="col-span-1">
                    <div className="flex flex-col">
                      <H5>Storefront availability</H5>
                      <H6>
                        Configure storefront availability and associated
                        customer notifications
                      </H6>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <StorefrontAvailabilityCard
                      supplierId={supplierQuery.data.id}
                    />
                  </div>
                </div>
              )}
          </>
        )}
      </div>
    </>
  );
};
